/**
 * RedlineExportCard — §13.7's fifth Verify assertion ("the redline export
 * button calls the redline route"), session 4 SHARED CONTRACT BLOCK 3 /
 * DECISION e / FACTS 3. The tab only ever sends `{ all: true }` — a
 * per-clause `clauseReviewIds` selection is a later refinement, not named by
 * any of the five Verify assertions.
 *
 * The response carries NO bytes (§9.4): it files a new `sent` `cta_versions`
 * row and returns its id + summary. The download is WP-32's presigned
 * `GET /versions/:id/download` (`api/workspace.ts`'s `downloadVersion`),
 * opened with `window.open(url, '_blank', 'noopener')` — never fetched
 * through the axios instance, never built by hand.
 */
import { useState } from 'react';
import { Download, FileSignature, Loader2 } from 'lucide-react';
import { primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { useToast } from '../../../ui/Toast';
import { describeWorkspaceApiError, downloadVersion } from '../../api/workspace';
import { describeClausesApiError, exportRedline, type RedlineExportResult } from '../../api/clauses';

interface RedlineExportCardProps {
  versionId: string;
}

export function RedlineExportCard({ versionId }: RedlineExportCardProps) {
  const { toast } = useToast();
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RedlineExportResult | null>(null);
  const [downloading, setDownloading] = useState(false);

  async function handleExport() {
    setExporting(true);
    setError(null);
    try {
      const res = await exportRedline(versionId, { all: true });
      setResult(res);
      toast(`Redline filed as ${res.filename}.`, { type: 'success' });
    } catch (err) {
      const message = describeClausesApiError('POST', `/api/contracts/versions/${versionId}/redline`, err);
      setError(message);
      toast(message, { type: 'error' });
    } finally {
      setExporting(false);
    }
  }

  async function handleDownload() {
    if (!result) return;
    setDownloading(true);
    try {
      const download = await downloadVersion(result.versionId);
      window.open(download.url, '_blank', 'noopener');
    } catch (err) {
      toast(describeWorkspaceApiError('GET', `/api/contracts/versions/${result.versionId}/download`, err), { type: 'error' });
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h4 className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 dark:text-gray-100">
            <FileSignature size={14} className="text-primary-600 dark:text-primary-400" /> Redline export
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Files every accepted clause change as a tracked-changes Word document (§9.4).
          </p>
        </div>
        <button type="button" data-testid="ctr-redline" className={primaryBtnCls} disabled={exporting} onClick={handleExport}>
          {exporting ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Exporting…
            </>
          ) : (
            'Export redline'
          )}
        </button>
      </div>

      {error && (
        <p data-testid="ctr-error" className="mt-2 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}

      {result && (
        <div data-testid="ctr-redline-summary" className="mt-3 space-y-2 text-xs">
          <div className="flex flex-wrap gap-x-4 gap-y-1 tabular-nums text-gray-700 dark:text-gray-300">
            <span>Clauses redlined: {result.summary.clausesRedlined}</span>
            <span>Changes applied: {result.summary.changesApplied}</span>
            <span>Insertions: {result.summary.insertions}</span>
            <span>Deletions: {result.summary.deletions}</span>
            <span>Comments: {result.summary.commentsWritten}</span>
          </div>
          {result.duplicateWarning && <p className="text-amber-700 dark:text-amber-400">{result.duplicateWarning}</p>}
          {result.summary.skipped.length > 0 && (
            <ul className="space-y-0.5">
              {result.summary.skipped.map((s, idx) => {
                const reason =
                  typeof s === 'object' && s !== null && 'reason' in s
                    ? String((s as { reason?: unknown }).reason)
                    : JSON.stringify(s);
                return (
                  <li key={idx} className="text-amber-700 dark:text-amber-400">
                    Skipped: {reason}
                  </li>
                );
              })}
            </ul>
          )}
          <button
            type="button"
            data-testid="ctr-redline-download"
            className={secondaryBtnCls}
            disabled={downloading}
            onClick={handleDownload}
          >
            <Download size={14} /> {downloading ? 'Preparing…' : 'Download'}
          </button>
        </div>
      )}
    </div>
  );
}

export default RedlineExportCard;
